import { useState, useEffect } from 'react';
import api from '../services/api';
import EditShipmentModal from './EditShipmentModal';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

function getDocProxyUrl(doc: any): string {
  // Convert FV document URL to our proxy endpoint
  const url = doc.downloadUrl || doc.url || '';
  // Extract path after freightview.com/v2.0/documents/
  const match = url.match(/\/v2\.0\/documents\/(.+)/);
  if (match) {
    return `${API_BASE}/freight/fv/documents/${match[1]}`;
  }
  // Also handle /documents/ without v2.0
  const match2 = url.match(/freightview\.com\/documents\/(.+)/);
  if (match2) {
    return `${API_BASE}/freight/fv/documents/${match2[1]}`;
  }
  // Also handle /shipper/documents/
  const match3 = url.match(/freightview\.com\/shipper\/documents\/(.+)/);
  if (match3) {
    return `${API_BASE}/freight/fv/documents/${match3[1]}`;
  }
  // Fallback: return as-is (external or already proxied)
  return url;
}

interface ShipmentDetailDrawerProps {
  order: any;
  onClose: () => void;
  onRefresh: () => void;
}

function getStatusBadge(status: string | null | undefined) {
  const s = (status || '').toLowerCase();
  if (s === 'delivered') {
    return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-green-900/50 text-green-400 border border-green-700">Delivered</span>;
  }
  if (s === 'in_transit') {
    return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-indigo-900/50 text-indigo-400 border border-indigo-700">In Transit</span>;
  }
  if (s === 'confirmed') {
    return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-900/50 text-blue-400 border border-blue-700">Booked</span>;
  }
  if (s === 'canceled') {
    return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-red-900/50 text-red-400 border border-red-700">Canceled</span>;
  }
  // unbooked / null / unknown
  return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-yellow-900/50 text-yellow-400 border border-yellow-700">Unbooked</span>;
}

function parseAddress(addr: any): { company?: string; address?: string; city?: string; state?: string; zip?: string } {
  if (!addr) return {};
  let parsed = addr;
  if (typeof addr === 'string') {
    try {
      parsed = JSON.parse(addr);
    } catch {
      return { address: addr };
    }
  }
  // Normalize: ship_to_address stores "street", FV uses "address"
  if (parsed.street && !parsed.address) {
    parsed = { ...parsed, address: parsed.street };
  }
  return parsed;
}

function formatCurrency(val: any): string {
  const num = Number(val);
  if (isNaN(num)) return '$0.00';
  return '$' + num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return dateStr;
  }
}

function getDocIcon(type: string | undefined) {
  const t = (type || '').toLowerCase();
  if (t === 'bol' || t.includes('bill')) {
    return (
      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  }
  if (t === 'label' || t.includes('label')) {
    return (
      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
      </svg>
    );
  }
  return (
    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

export default function ShipmentDetailDrawer({ order, onClose, onRefresh }: ShipmentDetailDrawerProps) {
  const [visible, setVisible] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [booking, setBooking] = useState(false);
  const [fvDetail, setFvDetail] = useState<any>(null);

  // Trigger slide-in on mount
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  // Load FV shipment detail (items, etc.) on mount
  useEffect(() => {
    if (order.freightview_shipment_id) {
      api.getFVShipmentDetail(order.freightview_shipment_id)
        .then((detail: any) => setFvDetail(detail))
        .catch(() => {}); // Silently fail if FV unavailable
    }
  }, [order.freightview_shipment_id]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleSyncTracking = async () => {
    setSyncing(true);
    try {
      await api.syncOrderTracking(order.id);
      onRefresh();
    } catch (error: any) {
      console.error('Failed to sync tracking:', error);
      alert(error.message || 'Failed to sync tracking');
    } finally {
      setSyncing(false);
    }
  };

  const handleCancelShipment = async () => {
    if (!confirm('Are you sure you want to cancel this shipment? This action cannot be undone.')) {
      return;
    }
    setCanceling(true);
    try {
      await api.cancelFVShipment(order.freightview_shipment_id);
      onRefresh();
    } catch (error: any) {
      console.error('Failed to cancel shipment:', error);
      alert(error.message || 'Failed to cancel shipment');
    } finally {
      setCanceling(false);
    }
  };

  const handleBook = async () => {
    if (!order.freightview_shipment_id || !order.selected_quote_id) {
      alert('This order does not have FreightView quote data.');
      return;
    }
    setBooking(true);
    try {
      const result = await api.bookWithFreightview({
        freightviewShipmentId: order.freightview_shipment_id,
        quoteId: order.selected_quote_id,
        orderId: order.id,
      });
      if (result.success) {
        onRefresh();
      } else {
        alert(`Booking failed: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Failed to book shipment:', error);
      alert(error.message || 'Failed to book shipment');
    } finally {
      setBooking(false);
    }
  };

  const dest = parseAddress(order.shipping_address);
  const freightStatus = (order.freight_tracking_status || order.freight_status || order.freightview_status || '').toLowerCase();
  const isDelivered = freightStatus === 'delivered';
  const isCanceled = freightStatus === 'canceled';
  const isBooked = freightStatus === 'confirmed' || freightStatus === 'in_transit' || isDelivered;

  const trackingEvents: any[] = (() => {
    if (!order.freight_tracking_events) return [];
    if (Array.isArray(order.freight_tracking_events)) return order.freight_tracking_events;
    if (typeof order.freight_tracking_events === 'string') {
      try { return JSON.parse(order.freight_tracking_events); } catch { return []; }
    }
    return [];
  })();

  const documents: any[] = (() => {
    if (!order.freight_documents) return [];
    if (Array.isArray(order.freight_documents)) return order.freight_documents;
    if (typeof order.freight_documents === 'string') {
      try { return JSON.parse(order.freight_documents); } catch { return []; }
    }
    return [];
  })();

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 ${visible ? 'bg-black/50' : 'bg-black/0 pointer-events-none'}`}
        onClick={handleBackdropClick}
      />

      {/* Drawer */}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-[480px] max-w-full flex flex-col bg-gray-800 shadow-2xl transform transition-transform duration-300 ease-in-out ${
          visible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-gray-900 border-b border-gray-700 px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-lg font-bold text-white truncate">
                  Order #{order.order_number}
                </h2>
                {getStatusBadge(freightStatus)}
              </div>
              {order.freightview_shipment_id && (
                <p className="text-sm text-gray-400">
                  FV Shipment: {order.freightview_shipment_id}
                </p>
              )}
              {order.freight_shipment_details_url && (
                <a
                  href={order.freight_shipment_details_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-2 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Open in FreightView
                </a>
              )}
            </div>
            <button
              onClick={handleClose}
              className="ml-4 p-1 text-gray-400 hover:text-white rounded transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Addresses */}
          <section>
            <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">Addresses</h3>
            <div className="grid grid-cols-2 gap-3">
              {/* Origin */}
              <div className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1.5">Origin</p>
                <p className="text-sm font-medium text-white">Keder Screens</p>
                <p className="text-sm text-gray-300">12446 State Hwy 205</p>
                <p className="text-sm text-gray-300">Lavon, TX 75166</p>
              </div>
              {/* Destination */}
              <div className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1.5">Destination</p>
                {dest.company && <p className="text-sm font-medium text-white">{dest.company}</p>}
                {dest.address && <p className="text-sm text-gray-300">{dest.address}</p>}
                {(dest.city || dest.state || dest.zip) && (
                  <p className="text-sm text-gray-300">
                    {[dest.city, dest.state].filter(Boolean).join(', ')} {dest.zip || ''}
                  </p>
                )}
                {!dest.address && !dest.city && (
                  <p className="text-sm text-gray-500 italic">No address on file</p>
                )}
              </div>
            </div>
          </section>

          {/* Shipment Items */}
          {fvDetail?.items && fvDetail.items.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">Shipment Items</h3>
              <div className="space-y-2">
                {fvDetail.items.map((item: any, idx: number) => (
                  <div key={idx} className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-white">{item.description || `Item ${idx + 1}`}</span>
                      {item.quantity && <span className="text-xs text-gray-400">Qty: {item.quantity}</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                      {item.weight && <span>{item.weight} lbs</span>}
                      {(item.length || item.width || item.height) && (
                        <span>{item.length || '-'}×{item.width || '-'}×{item.height || '-'} in</span>
                      )}
                      {item.freightClass && <span>Class {item.freightClass}</span>}
                      {item.nmfc && <span>NMFC {item.nmfc}</span>}
                      {item.packagingType && <span>{item.packagingType}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Booking Details */}
          {isBooked && (
            <section>
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">Booking Details</h3>
              <div className="bg-gray-900 rounded-lg p-4 border border-gray-700 space-y-2">
                {(order.freight_bol_number || order.bol_number) && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-400">BOL #</span>
                    <span className="text-sm text-white font-mono">{order.freight_bol_number || order.bol_number}</span>
                  </div>
                )}
                {(order.freight_pro_number || order.pro_number) && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-400">PRO #</span>
                    <span className="text-sm text-white font-mono">{order.freight_pro_number || order.pro_number}</span>
                  </div>
                )}
                {(order.freight_pickup_confirmation || order.pickup_confirmation) && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-400">Pickup Confirmation</span>
                    <span className="text-sm text-white font-mono">{order.freight_pickup_confirmation || order.pickup_confirmation}</span>
                  </div>
                )}
                {(order.tracking_number) && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-400">Tracking #</span>
                    <span className="text-sm text-white font-mono">{order.tracking_number}</span>
                  </div>
                )}
                {order.selected_carrier_name && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-400">Carrier</span>
                    <span className="text-sm text-white">{order.selected_carrier_name}</span>
                  </div>
                )}
                {order.freight_delivery_estimate && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-400">Est. Delivery</span>
                    <span className="text-sm text-white">{new Date(order.freight_delivery_estimate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                )}
                {order.freight_booked_at && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-400">Booked</span>
                    <span className="text-sm text-white">{new Date(order.freight_booked_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Cost Breakdown */}
          <section>
            <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">Cost Breakdown</h3>
            <div className="bg-gray-900 rounded-lg p-4 border border-gray-700 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-400">Base Amount</span>
                <span className="text-sm text-white">{formatCurrency(order.freight_base_amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-400">Markup</span>
                <span className="text-sm text-white">
                  {order.freight_markup_percent != null ? `${Number(order.freight_markup_percent).toFixed(0)}%` : '0%'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-400">Markup Amount</span>
                <span className="text-sm text-white">{formatCurrency(order.freight_markup_amount)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-700">
                <span className="text-sm font-semibold text-gray-300">Total (Dealer Pays)</span>
                <span className="text-sm font-bold text-green-400">{formatCurrency(order.freight_total_amount)}</span>
              </div>
            </div>
          </section>

          {/* Tracking Timeline */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Tracking Timeline</h3>
              <button
                onClick={handleSyncTracking}
                disabled={syncing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-400 bg-indigo-900/30 border border-indigo-700 rounded-lg hover:bg-indigo-900/50 disabled:opacity-50 transition-colors"
              >
                <svg className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {syncing ? 'Syncing...' : 'Sync Tracking'}
              </button>
            </div>

            {trackingEvents.length === 0 ? (
              <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                <p className="text-sm text-gray-500 italic text-center">No tracking data</p>
              </div>
            ) : (
              <div className="relative pl-6">
                {/* Vertical line */}
                <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-gray-700" />

                <div className="space-y-4">
                  {trackingEvents.map((event: any, idx: number) => (
                    <div key={idx} className="relative">
                      {/* Dot */}
                      <div className={`absolute -left-[17px] top-1.5 w-3 h-3 rounded-full border-2 ${
                        idx === 0 ? 'bg-indigo-500 border-indigo-400' : 'bg-gray-700 border-gray-600'
                      }`} />

                      <div className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-indigo-400 uppercase">
                            {event.status || 'Update'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(event.date || event.timestamp)}
                          </span>
                        </div>
                        {event.description && (
                          <p className="text-sm text-gray-300">{event.description}</p>
                        )}
                        {event.location && (
                          <p className="text-xs text-gray-500 mt-1">{event.location}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Documents */}
          <section>
            <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">Documents</h3>
            {documents.length === 0 ? (
              <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                <p className="text-sm text-gray-500 italic text-center">No documents</p>
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map((doc: any, idx: number) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between bg-gray-900 rounded-lg p-3 border border-gray-700"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {getDocIcon(doc.type)}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {doc.fileName || doc.name || doc.type || 'Document'}
                        </p>
                        {doc.type && (
                          <p className="text-xs text-gray-500 uppercase">{doc.type}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const url = getDocProxyUrl(doc);
                          const token = localStorage.getItem('shipping_token');
                          const resp = await fetch(url, {
                            headers: token ? { Authorization: `Bearer ${token}` } : {},
                          });
                          if (!resp.ok) throw new Error(`${resp.status}`);
                          const blob = await resp.blob();
                          const blobUrl = URL.createObjectURL(blob);
                          window.open(blobUrl, '_blank');
                        } catch (err: any) {
                          alert('Failed to load document: ' + (err.message || 'Unknown error'));
                        }
                      }}
                      className="flex-shrink-0 ml-3 px-3 py-1.5 text-xs font-medium text-indigo-400 bg-indigo-900/30 border border-indigo-700 rounded-lg hover:bg-indigo-900/50 transition-colors cursor-pointer"
                    >
                      View
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Actions footer */}
        <div className="flex-shrink-0 border-t border-gray-700 bg-gray-900 px-6 py-4">
          <div className="flex flex-wrap gap-2">
            {/* Edit Shipment */}
            <button
              onClick={() => setShowEditModal(true)}
              className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Shipment
            </button>

            {/* Book - only if unbooked */}
            {!isBooked && !isCanceled && (
              <button
                onClick={handleBook}
                disabled={booking}
                className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-green-700 border border-green-600 rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {booking ? 'Booking...' : 'Book'}
              </button>
            )}

            {/* Cancel Shipment - only if not canceled and not delivered */}
            {!isCanceled && !isDelivered && (
              <button
                onClick={handleCancelShipment}
                disabled={canceling}
                className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-red-700 border border-red-600 rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                {canceling ? 'Canceling...' : 'Cancel Shipment'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Edit Shipment Modal */}
      {showEditModal && (
        <EditShipmentModal
          order={order}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            setShowEditModal(false);
            onRefresh();
          }}
        />
      )}
    </>
  );
}
